/*!
 * lookbook.js — shared behaviour for the lookbook + drop calendar system.
 *
 * Used by:
 *   sections/drop-calendar-landing.liquid
 *   sections/drop-article.liquid
 *   sections/lookbook-landing-v2.liquid
 *   sections/lookbook-tab.liquid
 *   sections/shop-the-look-modal.liquid
 *
 * Loaded once via theme.liquid with `defer` (cached cross-page).
 *
 * Responsibilities:
 *   1. Tab switching for lookbook-landing
 *   2. Look card click → opens Shop the Look overlay (via Section
 *      Rendering API fetch for on-demand server-rendered panel content)
 *   3. Shop the Look close button + backdrop click → closes overlay
 *   4. Variant size buttons in the panel → triggers add-to-cart
 *   5. Klaviyo form-submit event hooks (Elevar dl_subscribe)
 *   6. Video lazy-pause via IntersectionObserver (pauses out-of-view
 *      videos to save CPU/battery on mobile)
 *
 * Idempotent via window.__lookbookInit (loads safely if Stiletto
 * re-registers sections after a re-render).
 */

(function () {
  if (window.__lookbookInit) return;
  window.__lookbookInit = true;

  // ============================================================
  // 1. Tab switching for lookbook landing
  // ============================================================

  function bindLookbookTabs(root) {
    var section = root.matches('[data-section-type="lookbook-landing"]')
                  ? root : root.querySelector('[data-section-type="lookbook-landing"]');
    if (!section) return;

    var buttons = section.querySelectorAll('[data-lookbook-tab-button]');
    var panels = section.querySelectorAll('[data-tab-index]');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = btn.getAttribute('data-tab-index');

        buttons.forEach(function (b) {
          var isActive = b.getAttribute('data-tab-index') === idx;
          b.classList.toggle('lookbook-landing__tab--active', isActive);
          b.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          var isActive = p.getAttribute('data-tab-index') === idx;
          if (isActive) p.removeAttribute('hidden');
          else p.setAttribute('hidden', '');
        });
      });
    });
  }

  function bindCollectionViewSwitcher(root) {
    var switcher = root.matches('[data-section-type="collection-view-switcher"]')
                   ? root : root.querySelector('[data-section-type="collection-view-switcher"]');
    if (!switcher) return;

    var buttons = switcher.querySelectorAll('[data-view-switcher-button]');

    function applyView(view) {
      // Find the product-grid section + the lookbook-tab section in the doc
      var productSection = document.querySelector('.shopify-section[id*="main-collection-product-grid"]')
                        || document.querySelector('[data-section-type="main-collection-product-grid"]')
                        || document.querySelector('.collection');
      var lookbookSection = document.querySelector('[data-section-type="lookbook-tab"]');

      // Walk up to the shopify-section wrapper for each
      function wrapper(el) {
        while (el && !el.classList.contains('shopify-section') && el !== document.body) el = el.parentElement;
        return el;
      }

      var productWrap = productSection ? wrapper(productSection) : null;
      var lookbookWrap = lookbookSection ? wrapper(lookbookSection) : null;

      if (view === 'lookbook') {
        if (productWrap) productWrap.setAttribute('hidden', '');
        if (lookbookWrap) lookbookWrap.removeAttribute('hidden');
      } else {
        if (productWrap) productWrap.removeAttribute('hidden');
        if (lookbookWrap) lookbookWrap.setAttribute('hidden', '');
      }

      buttons.forEach(function (b) {
        var isActive = b.getAttribute('data-view') === view;
        b.classList.toggle('collection-view-switcher__tab--active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyView(btn.getAttribute('data-view'));
      });
    });

    // Initial state: products view, or the lookbook when linked with #lookbook
    // (e.g. from the product page's Shop the look module).
    applyView(location.hash === '#lookbook' ? 'lookbook' : 'products');
  }

  function bindDropCalendarTabs(root) {
    var section = root.matches('[data-section-type="drop-calendar-landing"]')
                  ? root : root.querySelector('[data-section-type="drop-calendar-landing"]');
    if (!section) return;

    var buttons = section.querySelectorAll('[data-drops-tab-button]');
    var panels = section.querySelectorAll('[data-tab-panel]');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tab = btn.getAttribute('data-tab');

        buttons.forEach(function (b) {
          var isActive = b.getAttribute('data-tab') === tab;
          b.classList.toggle('drop-calendar-landing__tab--active', isActive);
          b.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          var isActive = p.getAttribute('data-tab-panel') === tab;
          if (isActive) p.removeAttribute('hidden');
          else p.setAttribute('hidden', '');
        });
      });
    });
  }

  // ============================================================
  // 2. Look card click → Shop the Look overlay
  // ============================================================

  function openLookOverlay(lookId) {
    if (!lookId) return;
    var modal = document.querySelector('[data-shop-the-look-modal]');
    if (!modal) {
      console.warn('[lookbook] Shop the Look modal section not mounted on this page');
      return;
    }

    var container = modal.querySelector('.shop-the-look-modal__container');

    // Find the pre-rendered panel template for this look (emitted alongside
    // each look-card in snippets/lookbook-look-card.liquid).
    var tpl = document.querySelector(
      'template[data-lookbook-look-template][data-look-id="' + lookId + '"]'
    );
    if (tpl && tpl.content) {
      container.innerHTML = '';
      container.appendChild(tpl.content.cloneNode(true));
      // Apply theme-editor label overrides
      var titleLabel = modal.getAttribute('data-title-label');
      var ctaLabel = modal.getAttribute('data-cta-label');
      var addLabel = modal.getAttribute('data-add-label');
      var rewardsPrefix = modal.getAttribute('data-rewards-prefix');
      var rewardsSuffix = modal.getAttribute('data-rewards-suffix');
      if (titleLabel) {
        var titleSlot = container.querySelector('[data-stl-title-slot]');
        if (titleSlot) titleSlot.textContent = titleLabel;
      }
      if (ctaLabel) {
        var ctaSlot = container.querySelector('[data-stl-cta-slot]');
        if (ctaSlot) ctaSlot.textContent = ctaLabel.toUpperCase();
      }
      if (addLabel) {
        container.querySelectorAll('[data-stl-add-single]').forEach(function (b) {
          b.textContent = addLabel.toUpperCase();
        });
      }
      var rewardsText = container.querySelector('[data-stl-rewards-text]');
      var rewardsRow = container.querySelector('[data-stl-rewards-slot]');
      if (rewardsText && rewardsRow) {
        var pts = rewardsRow.getAttribute('data-points');
        rewardsText.textContent = (rewardsPrefix || 'EARN') + ' ' + pts + ' ' + (rewardsSuffix || 'ENCLAVE POINTS');
      }
    } else {
      container.innerHTML =
        '<div style="padding:2rem">Look ' + lookId + ' not found on this page.</div>';
    }

    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    // RAF for transition trigger
    requestAnimationFrame(function () {
      modal.classList.add('is-open');
    });

    // Lock body scroll
    document.body.style.overflow = 'hidden';
  }

  function closeLookOverlay() {
    var modal = document.querySelector('[data-shop-the-look-modal]');
    if (!modal) return;
    modal.classList.remove('is-open');
    setTimeout(function () {
      modal.setAttribute('hidden', '');
      modal.setAttribute('aria-hidden', 'true');
      // Restore scroll
      document.body.style.overflow = '';
    }, 350); // match CSS transition duration
  }

  // Event delegation for all lookbook-look-card clicks site-wide
  document.addEventListener('click', function (e) {
    var card = e.target.closest('[data-lookbook-look-card]');
    // A card / hub tile that opens the Lookbook stories (data-stories-open) belongs to the stories
    // loader (snippets/lookbook-stories-loader) once it's on the page, except a preview-held tap,
    // which still goes to the phone step below. Without the viewer it opens Shop the Look as before.
    if (card && card.hasAttribute('data-stories-open') && window.RastahStories) {
      var Gs = window.RastahBFCMGate, Hs = document.documentElement.classList;
      var heldS = card.closest('[data-bfcm-drop="preview"]') && Hs.contains('bfcm-preview') &&
        !(Gs && Gs.previewUnlocked ? Gs.previewUnlocked() : Hs.contains('bfcm-preview-open'));
      if (!heldS) return;
    }
    if (card) {
      e.preventDefault();
      var lookId = card.getAttribute('data-look-id');
      // Preview week: a held look (blurred card, see snippets/look-preview-drop.liquid) or a look
      // holding a preview-drop piece would expose the drop; send them to the unlock instead.
      var G = window.RastahBFCMGate, H = document.documentElement.classList;
      if (G && typeof G.openPreview === 'function' && H.contains('bfcm-preview') && !(G.previewUnlocked && G.previewUnlocked())) {
        var tplPv = document.querySelector('template[data-lookbook-look-template][data-look-id="' + lookId + '"]');
        if (card.closest('[data-bfcm-drop="preview"]') || (tplPv && tplPv.content.querySelector('[data-bfcm-drop="preview"]'))) {
          G.openPreview('shop_the_look');
          return;
        }
      }
      openLookOverlay(lookId);
      // Fire Elevar dl_select_promotion event for analytics
      try {
        if (window.dataLayer && Array.isArray(window.dataLayer)) {
          window.dataLayer.push({
            event: 'dl_select_promotion',
            ecommerce: {
              creative_name: 'lookbook_look',
              creative_slot: card.getAttribute('data-look-n') ? 'Look ' + ('0' + card.getAttribute('data-look-n')).slice(-2) : '',
              promotion_id: lookId,
              promotion_name: 'Lookbook Look ' + lookId
            }
          });
        }
      } catch (_) { /* analytics never blocks UX */ }
      return;
    }

    if (e.target.closest('[data-shop-the-look-close]') ||
        e.target.matches('[data-shop-the-look-backdrop]')) {
      closeLookOverlay();
      return;
    }

    // Size button → mark as selected + update hidden variant input
    var sizeBtn = e.target.closest('[data-stl-size]');
    if (sizeBtn) {
      e.preventDefault();
      var available = sizeBtn.getAttribute('data-variant-available') === 'true';
      if (!available) return;
      var product = sizeBtn.closest('[data-stl-product]');
      if (!product) return;
      product.querySelectorAll('[data-stl-size]').forEach(function (b) {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
      });
      sizeBtn.classList.add('is-selected');
      sizeBtn.setAttribute('aria-checked', 'true');
      var variantInput = product.querySelector('[data-stl-variant-id]');
      if (variantInput) variantInput.value = sizeBtn.getAttribute('data-variant-id');
      return;
    }

    // Size guide button → swap in this product's size-fit modal, then let
    // size-fit-modal.js's own click handler open it (the button also has
    // data-open-size-fit so the bubble-phase handler will fire after this).
    var sizeGuideBtn = e.target.closest('[data-stl-size-guide]');
    if (sizeGuideBtn) {
      injectStlSizeFitModal(sizeGuideBtn);
      // don't preventDefault — bubble continues to size-fit-modal.js
      return;
    }

    // ADD TO BAG (single product within the panel)
    var singleAdd = e.target.closest('[data-stl-add-single]');
    if (singleAdd) {
      e.preventDefault();
      // BFCM price lock: a locked product goes through the email step first
      // (assets/bfcm-gate.js). require() runs the add straight away when the
      // device is already unlocked; the gate only exists in presale/preview.
      // Drop products: blurred in preview week / list-only in the early-access hour.
      // Each row is judged by its own product (its own data-bfcm-drop / data-bfcm-lock), never by
      // an element around it: on a product page the module sits inside .product, whose
      // attributes belong to the page's product (snippets/pdp-shop-the-look.liquid).
      var G = window.RastahBFCMGate;
      var stlRow = singleAdd.closest('[data-stl-product]');
      if (G && typeof G.canBuy === 'function' && stlRow && stlRow.hasAttribute('data-bfcm-drop') && !G.canBuy(stlRow)) {
        var was = singleAdd.textContent;
        singleAdd.textContent = document.documentElement.classList.contains('bfcm-ea') ? 'OPENS 1:00 PM EST' : 'SIZES OPEN AT LAUNCH';
        singleAdd.disabled = true;
        setTimeout(function () { singleAdd.textContent = was; singleAdd.disabled = false; }, 1800);
        return;
      }
      var lockedEl = singleAdd.closest('[data-stl-product][data-bfcm-lock]');
      if (lockedEl && window.RastahBFCMGate && typeof window.RastahBFCMGate.require === 'function') {
        window.RastahBFCMGate.require(function () { addSingleProduct(singleAdd); }, 'shop_the_look');
        return;
      }
      addSingleProduct(singleAdd);
      return;
    }
  });

  // ============================================================
  // Add single product to cart from STL panel
  // ============================================================
  function addSingleProduct(btn) {
    var product = btn.closest('[data-stl-product]');
    if (!product) return;
    var selected = product.querySelector('[data-stl-size].is-selected');
    if (!selected) {
      product.classList.add('is-missing-size');
      // brief visual feedback
      setTimeout(function () { product.classList.remove('is-missing-size'); }, 1500);
      return;
    }
    var variantId = product.querySelector('[data-stl-variant-id]').value;
    if (!variantId) return;

    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'ADDING…';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        btn.textContent = 'ADDED ✓';
        // For analytics on the surface that holds the row (e.g. the product page module).
        btn.dispatchEvent(new CustomEvent('stl:added', { bubbles: true, detail: { variantId: variantId } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Stiletto refreshes the cart count + quick cart on this event (nothing
        // listens for cart:refresh / theme.cart.refresh, so the bag never updated).
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
        // Stiletto's cart drawer hook
        try {
          if (window.theme && window.theme.cart && typeof window.theme.cart.refresh === 'function') {
            window.theme.cart.refresh();
          }
        } catch (_) {}
        setTimeout(function () {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 1200);
      })
      .catch(function (err) {
        console.error('[lookbook] add to cart failed', err);
        btn.textContent = 'TRY AGAIN';
        setTimeout(function () {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 1500);
      });
  }

  // ============================================================
  // Size guide — inject this product's size-fit modal into <body>
  // ============================================================
  // size-fit-modal.js uses `document.querySelector('[data-size-fit-modal]')`
  // to find a modal at click-time (lazy bindModal). We swap in the correct
  // product's modal before that lookup runs.
  function injectStlSizeFitModal(triggerBtn) {
    var product = triggerBtn.closest('[data-stl-product]');
    if (!product) return;
    var tpl = product.querySelector('[data-stl-size-fit-template]');
    if (!tpl || !tpl.content) return;

    // Already injected for this product? Don't dup.
    var clonedId = product.getAttribute('data-stl-size-fit-injected');
    if (clonedId) {
      // Make sure the live modal is the one for this product.
      var liveAlready = document.body.querySelector(
        '[data-size-fit-modal][data-stl-injected-for="' + clonedId + '"]'
      );
      if (liveAlready) return;
    }

    // Remove any other previously-injected STL size-fit modals to avoid
    // size-fit-modal.js binding to the wrong one.
    document.body
      .querySelectorAll('[data-size-fit-modal][data-stl-injected-for]')
      .forEach(function (el) { el.remove(); });

    // Reset size-fit-modal.js's cached modal so it rebinds to ours.
    if (window.__sizeFitResetModal && typeof window.__sizeFitResetModal === 'function') {
      window.__sizeFitResetModal();
    }

    var clone = tpl.content.cloneNode(true);
    var injectedModal = clone.querySelector('[data-size-fit-modal]');
    if (injectedModal) {
      var uid = 'stl-' + (product.getAttribute('data-product-id') || Date.now());
      injectedModal.setAttribute('data-stl-injected-for', uid);
      product.setAttribute('data-stl-size-fit-injected', uid);
    }
    document.body.appendChild(clone);
  }

  // ============================================================
  // Size guide (Kiwi Sizing — legacy fallback, kept for non-PDP contexts)
  // ============================================================
  // Mirrors the legacy collection-lookbook.liquid pattern. The Kiwi
  // global (`window.ks`) is loaded site-wide via snippets/kiwiSizing.liquid
  // in theme.liquid — we just need to call `loadSizing` for the clicked
  // product and trigger the auto-injected `.sizing-chart-modal-link`.
  //
  // To avoid re-mounting Kiwi on every click, we cache the mount per
  // product id via a data attribute on the trigger button.
  function openKiwiSizing(triggerBtn) {
    var product = triggerBtn.closest('[data-stl-product]');
    if (!product) return;
    var kiwiNode = product.querySelector('[data-stl-kiwi-data]');
    if (!kiwiNode) return;

    // If we've already mounted Kiwi for this trigger, just click the link.
    if (triggerBtn.hasAttribute('data-stl-kiwi-mounted')) {
      var existingLink = triggerBtn.querySelector('.sizing-chart-modal-link, .ks-chart-modal-link');
      if (existingLink) existingLink.click();
      return;
    }

    var kiwiData;
    try { kiwiData = JSON.parse(kiwiNode.textContent); } catch (_) { return; }

    function tryMount() {
      if (!window.ks || typeof window.ks.loadSizing !== 'function') return false;
      window.ks.loadSizing({
        productData: kiwiData,
        options: { el: triggerBtn, displayMode: 1 },
        loaded: function () {
          triggerBtn.setAttribute('data-stl-kiwi-mounted', '1');
          setTimeout(function () {
            var link = triggerBtn.querySelector('.sizing-chart-modal-link, .ks-chart-modal-link');
            if (link) link.click();
          }, 150);
        }
      });
      return true;
    }

    if (!tryMount()) {
      // Poll until Kiwi is available (loaded async from CDN)
      var attempts = 0;
      var iv = setInterval(function () {
        if (tryMount() || attempts++ > 30) clearInterval(iv);
      }, 200);
    }
  }

  // ============================================================
  // (removed) SELECT SIZES multi-add — redundant with per-product ADD TO BAG.
  // Keeping the function as a no-op to avoid undefined references if any
  // older cached template still has the button.
  // ============================================================
  function _unused_submitShopTheLook(ctaBtn) {
    var panel = ctaBtn.closest('[data-shop-the-look-panel]');
    if (!panel) return;
    var products = panel.querySelectorAll('[data-stl-product]');
    if (!products.length) return;

    // Clear stale validation states
    products.forEach(function (p) { p.classList.remove('is-missing-size'); });

    // Validate: every product must have a selected size
    var allSelected = true;
    var items = [];
    products.forEach(function (product) {
      var selected = product.querySelector('[data-stl-size].is-selected');
      if (!selected) {
        product.classList.add('is-missing-size');
        allSelected = false;
        return;
      }
      var id = product.querySelector('[data-stl-variant-id]').value;
      if (id) items.push({ id: parseInt(id, 10), quantity: 1 });
    });

    if (!allSelected) {
      var first = panel.querySelector('.is-missing-size');
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!items.length) return;

    ctaBtn.disabled = true;
    ctaBtn.textContent = 'ADDING…';
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        ctaBtn.textContent = 'ADDED ✓';
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Stiletto refreshes the cart count + quick cart on this event (nothing
        // listens for cart:refresh / theme.cart.refresh, so the bag never updated).
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
        try {
          if (window.theme && window.theme.cart && typeof window.theme.cart.refresh === 'function') {
            window.theme.cart.refresh();
          }
        } catch (_) {}
        setTimeout(function () {
          ctaBtn.disabled = false;
          ctaBtn.textContent = 'SELECT SIZES';
          closeLookOverlay();
        }, 800);
      })
      .catch(function (err) {
        console.error('[lookbook] multi-add to cart failed', err);
        ctaBtn.disabled = false;
        ctaBtn.textContent = 'TRY AGAIN';
        setTimeout(function () { ctaBtn.textContent = 'SELECT SIZES'; }, 1500);
      });
  }

  // Escape to close overlay
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var modal = document.querySelector('[data-shop-the-look-modal]:not([hidden])');
      if (modal) closeLookOverlay();
    }
  });

  // ============================================================
  // Deep link: ?look=<id> (from the product page's Shop the look
  // module, snippets/pdp-shop-the-look.liquid) scrolls the lookbook to
  // that look's card and outlines it briefly. Runs after the view
  // switchers (the hub shows its lookbook on #lookbook while parsing).
  // ============================================================
  function revealLinkedLook() {
    var m = /[?&]look=(\d+)/.exec(location.search);
    if (!m) return;
    var cards = document.querySelectorAll('[data-lookbook-look-card][data-look-id="' + m[1] + '"]');
    var card = null;
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].offsetParent !== null && !cards[i].closest('template')) { card = cards[i]; break; }
    }
    if (!card) return;
    var go = function () { card.scrollIntoView({ block: 'center' }); };
    go();
    card.style.outline = '1px solid currentColor';
    card.style.outlineOffset = '4px';
    setTimeout(function () { card.style.outline = ''; card.style.outlineOffset = ''; }, 2600);
    // Images above may still be settling; re-centre once on load unless the shopper has scrolled.
    var y = window.pageYOffset;
    window.addEventListener('load', function () { if (Math.abs(window.pageYOffset - y) < 4) go(); }, { once: true });
  }

  // Expose for explicit calls if needed
  window.LookbookOverlay = {
    open: openLookOverlay,
    close: closeLookOverlay
  };

  // ============================================================
  // 3. Klaviyo form-submit hook → Elevar dl_subscribe
  // ============================================================
  // Klaviyo fires `klaviyoForms` events on their global. We listen for
  // 'submit' events and push a `dl_subscribe` to the data layer so
  // Elevar can attribute the signup to the originating drop.

  function bindKlaviyoFormHooks() {
    var formContainers = document.querySelectorAll('[data-drop-signup-form]');
    if (!formContainers.length) return;

    // Klaviyo dispatches events via `klaviyoForms` global; subscribe
    // when it becomes available.
    var bound = false;
    function attemptBind() {
      if (bound) return;
      if (window.klaviyoForms && typeof window.klaviyoForms.addEventListener === 'function') {
        window.klaviyoForms.addEventListener('submit', function (event) {
          try {
            var formContainer = document.querySelector(
              '[data-drop-signup-form][data-form-id="' + event.formId + '"]'
            );
            if (!formContainer) return;
            var dropId = formContainer.closest('[data-drop-id]')
                         ? formContainer.closest('[data-drop-id]').getAttribute('data-drop-id')
                         : null;
            if (window.dataLayer && Array.isArray(window.dataLayer)) {
              window.dataLayer.push({
                event: 'dl_subscribe',
                klaviyo_form_id: event.formId,
                drop_id: dropId
              });
            }
          } catch (_) { /* analytics never blocks UX */ }
        });
        bound = true;
      }
    }

    attemptBind();
    if (!bound) {
      // Retry once Klaviyo has loaded
      var retries = 0;
      var retryInterval = setInterval(function () {
        attemptBind();
        retries++;
        if (bound || retries > 20) clearInterval(retryInterval);
      }, 500);
    }
  }
  bindKlaviyoFormHooks();

  // ============================================================
  // Drop tile countdown timer
  // ============================================================
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function updateCountdown(el) {
    var iso = el.getAttribute('data-release-at');
    if (!iso) return;
    var target = new Date(iso).getTime();
    var now = Date.now();
    var diff = Math.max(0, target - now);
    var days = Math.floor(diff / 86400000);
    var hours = Math.floor((diff % 86400000) / 3600000);
    var minutes = Math.floor((diff % 3600000) / 60000);
    var seconds = Math.floor((diff % 60000) / 1000);
    var d = el.querySelector('[data-countdown-days]');
    var h = el.querySelector('[data-countdown-hours]');
    var m = el.querySelector('[data-countdown-minutes]');
    var s = el.querySelector('[data-countdown-seconds]');
    if (d) d.textContent = pad(days);
    if (h) h.textContent = pad(hours);
    if (m) m.textContent = pad(minutes);
    if (s) s.textContent = pad(seconds);
    if (diff === 0) {
      // Reload to flip pre-drop → live state
      setTimeout(function () { location.reload(); }, 1000);
    }
  }

  function bindCountdowns() {
    var nodes = document.querySelectorAll('[data-drop-countdown]');
    if (!nodes.length) return;
    nodes.forEach(updateCountdown);
    setInterval(function () {
      document.querySelectorAll('[data-drop-countdown]').forEach(updateCountdown);
    }, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCountdowns);
  } else {
    bindCountdowns();
  }

  // ============================================================
  // 4. Video lazy-pause (perf + battery)
  // ============================================================

  if (typeof IntersectionObserver !== 'undefined') {
    var videoObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          if (video.paused) {
            video.play().catch(function () {/* autoplay blocked is fine */});
          }
        } else {
          if (!video.paused) video.pause();
        }
      });
    }, { rootMargin: '50px' });

    function observeVideos(root) {
      var videos = (root || document).querySelectorAll('[data-lookbook-video]');
      videos.forEach(function (v) { videoObserver.observe(v); });
    }
    observeVideos(document);

    // Also observe videos that show up later (Section Rendering API loads)
    if (typeof MutationObserver !== 'undefined') {
      var mo = new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          m.addedNodes.forEach(function (n) {
            if (n.nodeType === 1) observeVideos(n);
          });
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  // ============================================================
  // 5. Section registry hooks (Stiletto's theme.js pattern)
  // ============================================================
  // For consistency with Stiletto's section system, register our
  // sections so they bind correctly on theme-editor re-renders.

  if (window.Shopify && window.Shopify.theme && window.Shopify.theme.sections) {
    var registered = window.Shopify.theme.sections.registered;

    function registerLookbookSection(type, onLoad) {
      if (!registered[type]) {
        // We don't have access to Section constructor here, so we just
        // bind via init-on-load + DOM event delegation. The above event
        // listeners handle most interactions site-wide; this hook is for
        // section-local state like the tab switcher.
        document.addEventListener('shopify:section:load', function (e) {
          var container = e.target.querySelector('[data-section-type="' + type + '"]');
          if (container) onLoad(container);
        });
        // Also bind on initial page load
        document.querySelectorAll('[data-section-type="' + type + '"]').forEach(onLoad);
      }
    }

    registerLookbookSection('lookbook-landing', bindLookbookTabs);
    registerLookbookSection('drop-calendar-landing', bindDropCalendarTabs);
    registerLookbookSection('collection-view-switcher', bindCollectionViewSwitcher);
  } else {
    // Fallback: bind directly on DOM ready
    function init() {
      bindLookbookTabs(document);
      bindDropCalendarTabs(document);
      bindCollectionViewSwitcher(document);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', revealLinkedLook);
  } else {
    revealLinkedLook();
  }
})();
